import { useState } from 'react';
import { kickMarkerColor } from 'utilities/chartUtils';

type SessionCardPreviewMode = 'top' | 'goal';

export type SessionCardPreviewPoint = {
  xPct: number;
  yPct: number;
  result?: string;
  kind?: string;
};

export type SessionCardPreviewReachableSegment = {
  startXPct: number;
  startYPct: number;
  endXPct: number;
  endYPct: number;
  onTarget: number | null;
  measurementId: string;
};

type Props = {
  mode: SessionCardPreviewMode;
  points: SessionCardPreviewPoint[];
  reachableSegments?: SessionCardPreviewReachableSegment[];
  hideTopPoints?: boolean;
  onSelectMeasurement?: (id: string) => void;
};

const clampPct = (value: number): number => Math.max(0, Math.min(100, value));

const TOP_PREVIEW_BOUNDS = {
  left: 1,
  right: 98,
  top: 4,
  bottom: 96,
};

const TOP_VIEW_Z_MIN = -34;
const TOP_VIEW_Z_MAX = 34;
const TOP_VIEW_X_MIN = -3;
const TOP_VIEW_X_MAX = 58;

const FIELD_Z_MIN = -33;
const FIELD_Z_MAX = 33;
const GOAL_HALF_WIDTH = 3.66;
const GOAL_DEPTH = -2;
const PENALTY_BOX_HALF_WIDTH = 20.16;
const PENALTY_BOX_DEPTH = 16.5;
const GOAL_BOX_HALF_WIDTH = 9.16;
const GOAL_BOX_DEPTH = 5.5;
const PENALTY_SPOT_DEPTH = 11;
const HALFWAY_DEPTH = 52.5;
const CENTRE_CIRCLE_RADIUS = 9.15;

const topViewX = (z: number): number => {
  const pct = (z - TOP_VIEW_Z_MIN) / (TOP_VIEW_Z_MAX - TOP_VIEW_Z_MIN);
  return TOP_PREVIEW_BOUNDS.left + (pct * (TOP_PREVIEW_BOUNDS.right - TOP_PREVIEW_BOUNDS.left));
};

const topViewY = (x: number): number => {
  const pct = (x - TOP_VIEW_X_MIN) / (TOP_VIEW_X_MAX - TOP_VIEW_X_MIN);
  return TOP_PREVIEW_BOUNDS.top + (pct * (TOP_PREVIEW_BOUNDS.bottom - TOP_PREVIEW_BOUNDS.top));
};

// The preview stretches the pitch to fill its box, so a metre is a different number of viewBox units across and down.
const VIEWBOX_PER_METRE_Z = (TOP_PREVIEW_BOUNDS.right - TOP_PREVIEW_BOUNDS.left) / (TOP_VIEW_Z_MAX - TOP_VIEW_Z_MIN);
const VIEWBOX_PER_METRE_X = (TOP_PREVIEW_BOUNDS.bottom - TOP_PREVIEW_BOUNDS.top) / (TOP_VIEW_X_MAX - TOP_VIEW_X_MIN);

const previewXFromPct = (pct: number): number => (
  TOP_PREVIEW_BOUNDS.left + ((clampPct(pct) / 100) * (TOP_PREVIEW_BOUNDS.right - TOP_PREVIEW_BOUNDS.left))
);

const previewYFromPct = (pct: number): number => (
  TOP_PREVIEW_BOUNDS.top + ((clampPct(pct) / 100) * (TOP_PREVIEW_BOUNDS.bottom - TOP_PREVIEW_BOUNDS.top))
);

// NOTE: duplicated with SessionsPitchChart.tsx (onTargetColor) — differ in start colour and alpha, so not merged.
const reachableColor = (value: number | null): string => {
  const v = Math.max(0, Math.min(1, value ?? 0));
  const r = Math.round(168 + (34 - 168) * v);
  const g = Math.round(198 + (197 - 198) * v);
  const b = Math.round(228 + (94 - 228) * v);
  return `rgba(${r}, ${g}, ${b}, 0.82)`;
};

const SessionCardPreview = ({ mode, points, reachableSegments = [], hideTopPoints = false, onSelectMeasurement }: Props): React.ReactElement => {
  const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(null);
  const compactPoints = points.slice(0, 18);
  const compactSegments = reachableSegments.slice(0, 12);

  return (
    <div className={`sessions-focus-session-preview sessions-focus-session-preview--${mode}`}>
      <div className='sessions-focus-session-preview-stage'>
      {mode === 'top' && (
        <svg
          className='sessions-focus-session-preview-top-svg'
          viewBox='0 0 100 100'
          preserveAspectRatio='none'
          aria-hidden='true'
        >
          <g
            fill='none'
            stroke='#91a9c8'
            strokeWidth='0.85'
            strokeLinecap='square'
            strokeLinejoin='miter'
            vectorEffect='non-scaling-stroke'
          >
            <line x1={topViewX(FIELD_Z_MIN)} y1={topViewY(0)} x2={topViewX(FIELD_Z_MAX)} y2={topViewY(0)} />
            <line x1={topViewX(FIELD_Z_MIN)} y1={topViewY(0)} x2={topViewX(FIELD_Z_MIN)} y2={topViewY(TOP_VIEW_X_MAX)} />
            <line x1={topViewX(FIELD_Z_MAX)} y1={topViewY(0)} x2={topViewX(FIELD_Z_MAX)} y2={topViewY(TOP_VIEW_X_MAX)} />
            <line x1={topViewX(FIELD_Z_MIN)} y1={topViewY(HALFWAY_DEPTH)} x2={topViewX(FIELD_Z_MAX)} y2={topViewY(HALFWAY_DEPTH)} />
            <ellipse
              data-test='preview-centre-circle'
              cx={topViewX(0)}
              cy={topViewY(HALFWAY_DEPTH)}
              rx={CENTRE_CIRCLE_RADIUS * VIEWBOX_PER_METRE_Z}
              ry={CENTRE_CIRCLE_RADIUS * VIEWBOX_PER_METRE_X}
            />

            <line
              x1={topViewX(-PENALTY_BOX_HALF_WIDTH)}
              y1={topViewY(0)}
              x2={topViewX(-PENALTY_BOX_HALF_WIDTH)}
              y2={topViewY(PENALTY_BOX_DEPTH)}
            />
            <line
              x1={topViewX(PENALTY_BOX_HALF_WIDTH)}
              y1={topViewY(0)}
              x2={topViewX(PENALTY_BOX_HALF_WIDTH)}
              y2={topViewY(PENALTY_BOX_DEPTH)}
            />
            <line
              x1={topViewX(-PENALTY_BOX_HALF_WIDTH)}
              y1={topViewY(PENALTY_BOX_DEPTH)}
              x2={topViewX(PENALTY_BOX_HALF_WIDTH)}
              y2={topViewY(PENALTY_BOX_DEPTH)}
            />

            <line
              x1={topViewX(-GOAL_BOX_HALF_WIDTH)}
              y1={topViewY(0)}
              x2={topViewX(-GOAL_BOX_HALF_WIDTH)}
              y2={topViewY(GOAL_BOX_DEPTH)}
            />
            <line
              x1={topViewX(GOAL_BOX_HALF_WIDTH)}
              y1={topViewY(0)}
              x2={topViewX(GOAL_BOX_HALF_WIDTH)}
              y2={topViewY(GOAL_BOX_DEPTH)}
            />
            <line
              x1={topViewX(-GOAL_BOX_HALF_WIDTH)}
              y1={topViewY(GOAL_BOX_DEPTH)}
              x2={topViewX(GOAL_BOX_HALF_WIDTH)}
              y2={topViewY(GOAL_BOX_DEPTH)}
            />

            <line x1={topViewX(-GOAL_HALF_WIDTH)} y1={topViewY(GOAL_DEPTH)} x2={topViewX(-GOAL_HALF_WIDTH)} y2={topViewY(0)} />
            <line x1={topViewX(GOAL_HALF_WIDTH)} y1={topViewY(GOAL_DEPTH)} x2={topViewX(GOAL_HALF_WIDTH)} y2={topViewY(0)} />
            <line x1={topViewX(-GOAL_HALF_WIDTH)} y1={topViewY(GOAL_DEPTH)} x2={topViewX(GOAL_HALF_WIDTH)} y2={topViewY(GOAL_DEPTH)} />
          </g>
          <circle
            cx={topViewX(0)}
            cy={topViewY(PENALTY_SPOT_DEPTH)}
            r='0.65'
            fill='#91a9c8'
            vectorEffect='non-scaling-stroke'
          />
          {compactSegments.map((segment, index) => {
            const x1 = previewXFromPct(segment.startXPct);
            const y1 = previewYFromPct(segment.startYPct);
            const x2 = previewXFromPct(segment.endXPct);
            const y2 = previewYFromPct(segment.endYPct);
            const isHovered = hoveredSegmentId === segment.measurementId;
            return (
              <g
                key={`${index}-${x1}-${y1}-${x2}-${y2}`}
                style={{ cursor: onSelectMeasurement ? 'pointer' : 'default' }}
                onMouseEnter={() => setHoveredSegmentId(segment.measurementId)}
                onMouseLeave={() => setHoveredSegmentId(null)}
                onClick={() => onSelectMeasurement?.(segment.measurementId)}
              >
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke='rgba(56, 189, 248, 0.95)'
                  strokeWidth={isHovered ? '5' : '3.2'}
                  strokeLinecap='square'
                  vectorEffect='non-scaling-stroke'
                />
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={reachableColor(segment.onTarget)}
                  strokeWidth={isHovered ? '3.5' : '2.2'}
                  strokeLinecap='square'
                  vectorEffect='non-scaling-stroke'
                />
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke='transparent'
                  strokeWidth='10'
                  strokeLinecap='square'
                  vectorEffect='non-scaling-stroke'
                />
              </g>
            );
          })}
          {!hideTopPoints && compactPoints.map((point, index) => (
            <circle
              key={`${index}-${point.xPct}-${point.yPct}`}
              cx={previewXFromPct(point.xPct)}
              cy={previewYFromPct(point.yPct)}
              r='1.6'
              fill={kickMarkerColor({ result: point.result ?? '', kind: point.kind })}
              stroke='#ffffff'
              strokeWidth='0.7'
              vectorEffect='non-scaling-stroke'
            />
          ))}
        </svg>
      )}
      {mode === 'goal' && compactPoints.map((point, index) => (
        <span
          key={`${index}-${point.xPct}-${point.yPct}`}
          className='sessions-focus-session-preview-dot sessions-focus-session-preview-dot--goal'
          style={{
            left: `${clampPct(point.xPct)}%`,
            top: `${clampPct(point.yPct)}%`,
            background: kickMarkerColor({ result: point.result ?? '', kind: point.kind }),
          }}
        />
      ))}
      </div>
    </div>
  );
};

export default SessionCardPreview;